/**
 * Zwei Sprachen für die Oberfläche.
 *
 * Übersetzt wird, was die App sagt, nicht was die Regel sagt: die Regeltexte bleiben in
 * der Sprache ihrer Quelle (englisch), weil eine Übersetzung eine Behauptung über den
 * Inhalt wäre, die niemand geprüft hat. Die Bestandsdaten führen beide Sprachen ohnehin
 * (`{ de, en }`), und `pick` holt daraus die passende.
 *
 * Das deutsche Wörterbuch ist die Vorlage, das englische muss dieselben Schlüssel haben.
 * Fehlt einer, meckert schon der Typ, und `test/i18n.test.ts` prüft es noch einmal.
 */

export type Lang = 'de' | 'en'

export const LANGS: Lang[] = ['de', 'en']

export const de = {
  'app.title': 'Navigation Lights 3D',
  'mode.night': 'Nacht',
  'mode.day': 'Tag',
  'mode.red': 'Rot',
  'nav.lights': 'Lichter',
  'nav.buoys': 'Seezeichen',
  'nav.scenarios': 'Ausweichen',
  'nav.signals': 'Signale',
  'nav.quiz': 'Quiz',
  'nav.progress': 'Fortschritt',
  'nav.settings': 'Einstellungen',
  'region.button': 'Region {region}',
  'cards.auftrag': 'Sieh dir das Bild an und sag, was es ist. Dann deck auf.',
  'exam.auftrag': 'Starte einen Durchgang und beantworte ihn wie in der Prüfung.',
  'buoys.auftrag': 'Wähle ein Seezeichen und sieh dir seine Kennung an.',
  'scenarios.auftrag': 'Sieh aus deinem Fenster und entscheide, wer ausweicht.',
  'signals.auftrag': 'Spiel ein Signal ab und präg dir ein, was es heißt.',
  'progress.auftrag': 'Sieh nach, wo es klemmt, und üb genau das.',
  'progress.weakest': 'Am schwächsten sitzt gerade: {group}, {rate} Prozent richtig.',
  'korb.neu': 'noch nie dran',
  'korb.wackelt': 'sitzt noch nicht',
  'korb.sitzt': 'sitzt',
  'korb.fehler': 'zuletzt falsch',
  'ueben.auftrag': 'Tippe einen Korb an, oder beantworte die Frage darunter.',
  'ueben.leer': 'Alles beantwortet. Nimm einen anderen Korb oder den Prüfungsbogen.',
  'einstieg.was':
    'Ein Trainer für die Lichter, Zeichen und Signale auf dem Wasser. Jedes Bild steht in 3D und trägt die Regel, aus der es stammt.',
  'einstieg.frage': 'Was willst du können?',
  'einstieg.binnen': 'Prüfung Binnen bestehen',
  'einstieg.binnen.sagt': 'Sportbootführerschein Binnen. Du lernst die Binnenschifffahrtsstraßen-Ordnung.',
  'einstieg.see': 'Prüfung See bestehen',
  'einstieg.see.sagt': 'Sportbootführerschein See. Du lernst die Kollisionsverhütungsregeln.',
  'einstieg.nachschlagen': 'Nur nachschlagen',
  'einstieg.nachschlagen.sagt': 'Du fährst schon und willst wissen, was du da vor dir siehst.',
  'einstieg.spaeter': 'Die Wahl lässt sich jederzeit ändern.',
  'nav.ueben': 'Üben',
  'nav.bestimmen': 'Bestimmen',
  'nav.pruefen': 'Prüfen',
  'nav.einstellungen': 'Einstellungen',
  'bestimmen.auftrag': 'Tippe an, was du siehst. Die Liste darunter wird kürzer.',
  'viewer.hinweis': 'Dreh die Peilung, bis du siehst, wie die Lichter von der Seite stehen. Die Entfernung zeigt, welches Licht zuerst verschwindet.',
  'viewer.hinweis.scenario': 'Dreh den Blick, bis du das andere Fahrzeug im Fenster hast.',
  'bestimmen.suche': 'Oder nach Namen suchen: Schlepp, Anker, Rule 27 ...',
  'bestimmen.suche.binnen': 'Oder nach Namen suchen: Fähre, Anker, § 3.14 ...',
  'bestimmen.farben': 'Farben',
  'bestimmen.weisse': 'Weiße',
  'bestimmen.weisse.drei': '3 oder mehr',
  'bestimmen.dazu': 'Dazu',
  'bestimmen.uebereinander': 'zwei übereinander',
  'bestimmen.blinkt': 'blinkt',
  'bestimmen.tagzeichen': 'Tagzeichen',
  'bestimmen.treffer': '{treffer} von {alle} Bildern passen',
  'bestimmen.zuruecksetzen': 'Von vorn',
  'bestimmen.leer': 'Kein Bild führt diese Lichter zusammen. Nimm ein Merkmal weg.',
  'bestimmen.gesetzt': 'Du siehst',
  'bestimmen.folge': 'Übereinander',
  'bestimmen.folge.ueber': 'über',
  'bestimmen.topp': 'Topplichter',
  'bestimmen.rundum': 'Rundumlichter',
  'bestimmen.seitenlicht': 'Seitenlicht',
  'bestimmen.seitenlicht.ja': 'sehe ich',
  'bestimmen.seitenlicht.nein': 'keins',
  'bestimmen.blinkt.ja': 'blinkt',
  'bestimmen.blinkt.nein': 'ruhig',
  'bestimmen.keins': 'keins',
  'bestimmen.tagform': 'Form',
  'bestimmen.sichtfrei': 'Ich sehe das Fahrzeug frei und ganz',
  'bestimmen.sichtfrei.hilfe':
    'Dann gelten deine Angaben genau: die Farben sind alle Farben, die Zahlen sind die Zahlen. Ohne das zählt alles als Untergrenze, weil ein Sektor oder ein Aufbau ein Licht verdecken kann.',
  'bestimmen.schluss':
    'Weiter geht es nicht: diese Fahrzeuge führen dieselben Lichter. Die Regel unterscheidet sie, das Bild nicht.',
  'bestimmen.naechste': 'Nächste Frage',
  'farbe.white': 'weiß',
  'farbe.red': 'rot',
  'farbe.green': 'grün',
  'farbe.yellow': 'gelb',
  'farbe.blue': 'blau',
  'bereich.label': 'Bereich',
  'bereich.see': 'See',
  'bereich.binnen': 'Binnen',
  'bereich.see.note':
    'Kollisionsverhütungsregeln (KVR), international und in den US-Binnengewässern.',
  'bereich.binnen.note':
    'Binnenschifffahrtsstraßen-Ordnung, Kapitel 3. Gilt auf den deutschen Binnenwasserstraßen und weicht von der See ab. Normtext und Erklärungen stehen nur auf Deutsch: eine amtliche englische Fassung gibt es nicht.',
  'detail.disclaimer.binnen':
    'Normtext aus der Binnenschifffahrtsstraßen-Ordnung, gemeinfrei nach § 5 UrhG. Der Text und die Erklärung stehen nur auf Deutsch, weil es keine amtliche englische Fassung gibt.',
  'scope.international': 'nur international',
  'scope.inland': 'nur US-Binnen',
  'buoy.count': '{count} Seezeichen in Region {region}',
  'buoy.category.lateral': 'Laterale Zeichen',
  'buoy.category.preferred-channel': 'Abzweigende Fahrwasser',
  'buoy.category.cardinal': 'Kardinale Zeichen',
  'buoy.category.isolated-danger': 'Einzelgefahrenstelle',
  'buoy.category.safe-water': 'Mittefahrwasser',
  'buoy.category.special': 'Sonderzeichen',
  'buoy.category.mooring': 'Festmachetonne',
  'detail.source': 'Quelle',
  'detail.disclaimer.rules':
    'Lernwerkzeug, kein Ersatz für Navigation. Ohne Gewähr auf Vollständigkeit. Regeltext: {source}',
  'detail.disclaimer.buoys': 'Lernwerkzeug, kein Ersatz für Navigation. Quellen: {sources}',
  'detail.source.navrules': 'Navigation Rules der US Coast Guard, gemeinfrei',
  'inland.summary': 'In den US-Binnenregeln weicht Rule {rule} ab: {ids}',
  'inland.whole': 'ganze Regel',
  'inland.none': 'gilt in den Binnenregeln nicht',
  'viewer.bearing': 'Peilung',
  'viewer.look': 'Blick',
  'viewer.lookDirection': 'Blickrichtung',
  'viewer.distance': 'Entfernung',
  'progress.summary': '{seen} von {total} Karten schon einmal gesehen. Schwachstellen stehen oben.',
  'progress.group.rule': 'Regel {rule}',
  'progress.group.signals': 'Schallsignale, {group}',
  'quiz.right': 'Richtig.',
  'quiz.wrong': 'Falsch. Richtig ist: {answer}',
  'quiz.next': 'Weiter',
  'quiz.empty': 'Kein Stoff vorhanden.',
  'quiz.signal.play': 'Signal abspielen',
  'quiz.signal.repeat': 'Im Nebel wiederholt sich das Signal.',
  'quiz.signal.once': 'Einmalig gegeben.',
  'scenarios.prompt': 'Wer weicht aus?',
  'scenarios.wind': 'Wind aus {wind} Grad. Dein Kurs: {heading} Grad.',
  'giveway.own': 'Ich weiche aus',
  'giveway.other': 'Der andere weicht aus',
  'giveway.both': 'Beide handeln',
  'giveway.neither': 'Keiner ist ausweichpflichtig',
  'signals.group.manoeuvre': 'Manöver',
  'signals.group.manoeuvre.hint': 'In Sicht voneinander, Regel 34(a)',
  'signals.group.warning': 'Warnung',
  'signals.group.warning.hint': 'Zweifel am Verhalten des anderen, Regel 34(d)',
  'signals.group.narrow-channel': 'Enges Fahrwasser',
  'signals.group.narrow-channel.hint': 'Überholen und Bugsignal, Regel 34(c) und (e)',
  'signals.group.restricted-visibility': 'Unsichtigkeit',
  'signals.group.restricted-visibility.hint': 'Nebel und Dunst, Regel 35',
  'signals.group.identity': 'Kennung',
  'signals.group.identity.hint': 'Wer bin ich, Regel 35(k)',
  'signals.group.distress': 'Notsignale',
  'signals.group.distress.hint': 'Regel 37 mit Anhang IV',
  'signals.step.short': 'kurz',
  'signals.step.prolonged': 'lang',
  'signals.step.continuous': 'Dauerton',
  'signals.step.bell': 'Glocke',
  'signals.step.gong': 'Gong',
  'signals.step.silence': 'Pause',
  'signals.length': '{steps} · {seconds} Sekunden',
  'signals.play': 'Abspielen',
  'signals.stop': 'Stopp',
  'signals.repeat.once': 'Einmalig, sobald die Lage es verlangt.',
  'signals.repeat.minute': 'Wiederholung: jede Minute, solange die Lage andauert.',
  'signals.repeat.minutes': 'Wiederholung: alle {minutes} Minuten, solange die Lage andauert.',
  'signals.voice.gross': 'Schiff ab 200 Metern',
  'signals.voice.mittel': 'Schiff 75 bis 200 Meter',
  'signals.voice.klein': 'Schiff unter 75 Metern',
  'signals.voice.title': '{low} bis {high} Hertz nach Anhang III',
  'signals.voice.note':
    'Tonhöhe {hz} Hertz. Anhang III schreibt für {name} {low} bis {high} Hertz vor, damit man an der Tonhöhe die Größe des anderen hört.',
  'signals.disclaimer':
    'Der Ton entsteht im Browser aus dem Muster der Regel, er ist keine Aufnahme eines Schiffes. Lernwerkzeug, kein Ersatz für Navigation.',
  'settings.title': 'Einstellungen',
  'settings.language': 'Sprache',
  'settings.language.de': 'Deutsch',
  'settings.language.en': 'English',
  'settings.region': 'IALA-Region',
  'settings.region.hint':
    'Der Bestand folgt zwei Quellen: Region A der deutschen Seeschifffahrtsstraßen-Ordnung, Region B dem amerikanischen 33 CFR 62. Welche Region wo gilt, steht in der Seekarte.',
  'settings.units': 'Einheiten',
  'settings.units.nm': 'Seemeilen',
  'settings.units.km': 'Kilometer',
  'binnenSignale.wer.bergfahrer': 'Gibt der Bergfahrer, das Fahrzeug in Bergfahrt: auf einem Fluss zur Quelle hin.',
  'binnenSignale.wer.talfahrer': 'Gibt der Talfahrer, das Fahrzeug in Talfahrt: auf einem Fluss mit dem Strom.',
  'binnenSignale.wer.ueberholender': 'Gibt das Fahrzeug, das überholen will.',
  'binnenSignale.wer.vorausfahrender': 'Gibt das Fahrzeug, das überholt werden soll.',
  'binnenSignale.antworten': 'Mögliche Antworten',
  'binnenSignale.fahrzeug.maschine': 'Maschinenfahrzeug',
  'binnenSignale.fahrzeug.klein': 'Kleinfahrzeug',
  'binnenSignale.antwortAuf': 'Antwort auf:',
  'binnenSignale.schweigen': 'Kein Zeichen: hier ist das Schweigen die Zustimmung, und das steht so in der Ordnung, es ist keine Lücke der Tabelle.',
  'binnenSignale.schweigen.kurz': 'Kein Zeichen',
  'binnenSignale.minute': 'Längstens jede Minute wiederholt.',
  'binnenSignale.wiederholt': 'Wiederholt, solange die Lage andauert.',
  'binnenSignale.licht': 'Ein Maschinenfahrzeug gibt dazu ein gelbes Lichtzeichen gleicher Dauer, hell und von allen Seiten sichtbar (§ 4.01 Nummer 2).',
  'binnenSignale.licht.glocke': 'Ohne Lichtzeichen: Glockenzeichen sind nach § 4.01 Nummer 2 ausgenommen.',
  'binnenSignale.licht.schweigen': 'Ohne Lichtzeichen: wo kein Schallzeichen ist, ist auch keines.',
  'binnenSignale.kleinfahrzeug.ja': 'Auch ein Kleinfahrzeug darf dieses Zeichen geben (§ 4.02 Nummer 2).',
  'binnenSignale.kleinfahrzeug.nein': 'Ein Kleinfahrzeug gibt dieses Zeichen nicht: § 4.02 Nummer 2 erlaubt ihm nur die Allgemeinen Zeichen aus Abschnitt A.',
  'signals.auftrag.binnen': 'Tippe ein Zeichen an, hör es dir an und lies, wer es gibt und was darauf folgt.',
  'settings.mondphase': 'Mondphase',
  'viewer.mondphase.hinweis':
    'Der Mond entscheidet, wie schwer es ist: bei Vollmond siehst du die Gestalt des Fahrzeugs, bei Neumond nur seine Lichter. So sieht es auf dem Wasser aus.',
  'settings.mondphase.hint':
    'Der Mond entscheidet, wie viel man nachts vom Rumpf sieht. Bei Neumond bleiben nur die Lichter, und genau darauf kommt es an.',
  'mondphase.neu': 'Neumond',
  'mondphase.sichel': 'Sichel',
  'mondphase.halb': 'Halbmond',
  'mondphase.dreiviertel': 'Dreiviertel',
  'mondphase.voll': 'Vollmond',
  'settings.view': 'Ansicht',
  'settings.reset': 'Lernstand löschen',
  'settings.reset.done': 'Lernstand gelöscht.',
  'settings.storage': 'Alles bleibt auf diesem Gerät. Die App sendet nichts ins Netz.',
  'settings.offline.ready': 'Für die Nutzung ohne Netz bereit.',
  'settings.offline.pending': 'Wird für die Nutzung ohne Netz vorbereitet.',
  'settings.offline.off': 'Ohne Netz nicht verfügbar: dieser Browser kann es nicht.',
  'nav.cards': 'Karten',
  'cards.prompt': 'Was zeigt dieses Bild?',
  'cards.prompt.signal': 'Welches Signal ist das?',
  'cards.reveal': 'Aufdecken',
  'cards.known': 'Gewusst',
  'cards.unknown': 'Nicht gewusst',
  'app.disclaimer':
    'Lernwerkzeug, kein Ersatz für Navigation. Alle Angaben stammen aus gemeinfreien Regeltexten und stehen mit Fundstelle dabei.',
  'exam.title': 'Prüfungsmodus',
  'exam.intro':
    'Ein Bogen wie in der amtlichen Prüfung: {questions} Fragen, {minutes} Minuten, vier Antworten je Frage, eine davon richtig. Die Fragen kommen aus dem Bestand dieser App, nicht aus dem amtlichen Fragenkatalog.',
  'exam.part': '{name}: {questions} Fragen, bestanden ab {min} Punkten',
  'exam.skipped':
    'Nicht dabei: {name} mit {questions} Fragen. Dafür braucht es Seekarte, Zirkel und Anlegedreieck, und die nimmt dir diese App nicht ab.',
  'exam.source': 'Zahlen aus: {title}, Stand {stand}.',
  'exam.area.see': 'Seeschifffahrt',
  'exam.area.binnen': 'Binnenschifffahrt',
  'exam.binnen.note':
    'Der spezifische Teil zieht seine Fragen aus der Binnenschifffahrtsstraßen-Ordnung, Kapitel 3, jede mit ihrer Fundstelle. Der Basisteil kommt aus dem gemeinsamen Grundstoff, also Lichterführung und Schallsignale: die Schallsignale der Ordnung stehen in Kapitel 4 und sind hier noch nicht im Bestand.',
  'exam.start': 'Durchgang starten',
  'exam.position': 'Frage {at} von {of} · {part}',
  'exam.back': 'Zurück',
  'exam.next': 'Weiter',
  'exam.finish': 'Abgeben',
  'exam.open': 'noch offen: {open}',
  'exam.passed': 'Bestanden.',
  'exam.failed': 'Nicht bestanden.',
  'exam.result.part': '{name}: {points} von {questions} Punkten, nötig waren {min}',
  'exam.result.points': 'Zusammen {points} von {questions} Punkten. Jeder Teil zählt für sich.',
  'exam.citations': 'Woher die Fragen kamen',
  'exam.wrong': 'Das saß noch nicht',
  'exam.again': 'Neuer Durchgang',
  'nav.exam': 'Prüfung',
  'paywall.title': 'Vollversion',
  'paywall.body':
    'Gratis siehst du die Lichterbilder nach Regel 23 bis 31 und zehn Übungsfragen. Seezeichen, Ausweichsituationen, Schallsignale, Fortschritt und der ganze Fragenbestand gehören zur Vollversion.',
  'paywall.price': 'Einmalig {price} Euro, kein Abo.',
  'paywall.stub':
    'Der Kauf läuft später über den Store. In dieser Fassung schaltet der Knopf ohne Zahlung frei.',
  'paywall.buy': 'Vollversion freischalten',
  'paywall.restore': 'Kauf wiederherstellen',
  'paywall.unlocked': 'Vollversion ist freigeschaltet.',
  'paywall.quiz.limit': 'Zehn Gratisfragen sind aufgebraucht.',
} as const

export type Key = keyof typeof de

export const en: Record<Key, string> = {
  'app.title': 'Navigation Lights 3D',
  'mode.night': 'Night',
  'mode.day': 'Day',
  'mode.red': 'Red',
  'nav.lights': 'Lights',
  'nav.buoys': 'Marks',
  'nav.scenarios': 'Give way',
  'nav.signals': 'Signals',
  'nav.quiz': 'Quiz',
  'nav.progress': 'Progress',
  'nav.settings': 'Settings',
  'region.button': 'Region {region}',
  'cards.auftrag': 'Look at the picture and say what it is. Then reveal.',
  'exam.auftrag': 'Start a run and answer it as you would in the exam.',
  'buoys.auftrag': 'Pick a mark and look at its rhythm.',
  'scenarios.auftrag': 'Look out of your window and decide who gives way.',
  'signals.auftrag': 'Play a signal and learn what it means.',
  'progress.auftrag': 'See where it sticks, and practise exactly that.',
  'progress.weakest': 'Weakest right now: {group}, {rate} per cent correct.',
  'korb.neu': 'never seen',
  'korb.wackelt': 'not sitting yet',
  'korb.sitzt': 'sitting',
  'korb.fehler': 'wrong last time',
  'ueben.auftrag': 'Tap a basket, or answer the question below.',
  'ueben.leer': 'All answered. Take another basket or the exam paper.',
  'einstieg.was':
    'A trainer for the lights, marks and sound signals on the water. Every picture stands in 3D and carries the rule it comes from.',
  'einstieg.frage': 'What do you want to be able to do?',
  'einstieg.binnen': 'Pass the inland exam',
  'einstieg.binnen.sagt': 'German inland licence. You learn the inland waterway rules.',
  'einstieg.see': 'Pass the sea exam',
  'einstieg.see.sagt': 'German sea licence. You learn the collision regulations.',
  'einstieg.nachschlagen': 'Just look things up',
  'einstieg.nachschlagen.sagt': 'You already sail and want to know what you are looking at.',
  'einstieg.spaeter': 'You can change this at any time.',
  'nav.ueben': 'Practise',
  'nav.bestimmen': 'Identify',
  'nav.pruefen': 'Exam',
  'nav.einstellungen': 'Settings',
  'bestimmen.auftrag': 'Tap what you see. The list below gets shorter.',
  'viewer.hinweis': 'Turn the bearing until you see how the lights stand from the side. The distance shows which light disappears first.',
  'viewer.hinweis.scenario': 'Turn your view until the other vessel is in your window.',
  'bestimmen.suche': 'Or search by name: towing, anchor, rule 27 ...',
  'bestimmen.suche.binnen': 'Or search by name: ferry, anchor, § 3.14 ...',
  'bestimmen.farben': 'Colours',
  'bestimmen.weisse': 'White',
  'bestimmen.weisse.drei': '3 or more',
  'bestimmen.dazu': 'Also',
  'bestimmen.uebereinander': 'two above each other',
  'bestimmen.blinkt': 'flashing',
  'bestimmen.tagzeichen': 'day shape',
  'bestimmen.treffer': '{treffer} of {alle} pictures match',
  'bestimmen.zuruecksetzen': 'Start over',
  'bestimmen.gesetzt': 'You see',
  'bestimmen.folge': 'Above each other',
  'bestimmen.folge.ueber': 'over',
  'bestimmen.topp': 'Masthead lights',
  'bestimmen.rundum': 'All-round lights',
  'bestimmen.seitenlicht': 'Sidelight',
  'bestimmen.seitenlicht.ja': 'I see one',
  'bestimmen.seitenlicht.nein': 'none',
  'bestimmen.blinkt.ja': 'flashing',
  'bestimmen.blinkt.nein': 'steady',
  'bestimmen.keins': 'none',
  'bestimmen.tagform': 'Shape',
  'bestimmen.sichtfrei': 'I see the vessel clear and whole',
  'bestimmen.sichtfrei.hilfe':
    'Then your answers count exactly: the colours are all the colours, the numbers are the numbers. Without it everything counts as a lower bound, because a sector or a superstructure can hide a light.',
  'bestimmen.schluss':
    'This is as far as it goes: these vessels carry the same lights. The rule tells them apart, the picture does not.',
  'bestimmen.naechste': 'Next question',
  'bestimmen.leer': 'No picture carries these lights together. Take one trait away.',
  'farbe.white': 'white',
  'farbe.red': 'red',
  'farbe.green': 'green',
  'farbe.yellow': 'yellow',
  'farbe.blue': 'blue',
  'bereich.label': 'Area',
  'bereich.see': 'Sea',
  'bereich.binnen': 'Inland',
  'bereich.see.note':
    'Collision regulations (COLREGs), international and US inland.',
  'bereich.binnen.note':
    'German inland waterway rules (BinSchStrO), chapter 3. They apply on German inland waterways and differ from the rules at sea. Rule text and explanations are German only: there is no official English version.',
  'detail.disclaimer.binnen':
    'Rule text from the German inland waterway rules (BinSchStrO), public domain under § 5 UrhG. Text and explanation are German only, because there is no official English version.',
  'scope.international': 'international only',
  'scope.inland': 'US inland only',
  'buoy.count': '{count} marks in region {region}',
  'buoy.category.lateral': 'Lateral marks',
  'buoy.category.preferred-channel': 'Preferred channel marks',
  'buoy.category.cardinal': 'Cardinal marks',
  'buoy.category.isolated-danger': 'Isolated danger mark',
  'buoy.category.safe-water': 'Safe water mark',
  'buoy.category.special': 'Special marks',
  'buoy.category.mooring': 'Mooring buoy',
  'detail.source': 'Source',
  'detail.disclaimer.rules':
    'A learning tool, not a substitute for navigation. No guarantee of completeness. Rule text: {source}',
  'detail.disclaimer.buoys': 'A learning tool, not a substitute for navigation. Sources: {sources}',
  'detail.source.navrules': 'Navigation Rules of the US Coast Guard, public domain',
  'inland.summary': 'The US inland rules differ in Rule {rule}: {ids}',
  'inland.whole': 'whole rule',
  'inland.none': 'does not apply inland',
  'viewer.bearing': 'Bearing',
  'viewer.look': 'Looking',
  'viewer.lookDirection': 'Look direction',
  'viewer.distance': 'Distance',
  'progress.summary': '{seen} of {total} cards seen at least once. Weak spots first.',
  'progress.group.rule': 'Rule {rule}',
  'progress.group.signals': 'Sound signals, {group}',
  'quiz.right': 'Correct.',
  'quiz.wrong': 'Wrong. The answer is: {answer}',
  'quiz.next': 'Next',
  'quiz.empty': 'Nothing to learn here yet.',
  'quiz.signal.play': 'Play signal',
  'quiz.signal.repeat': 'In fog this signal repeats.',
  'quiz.signal.once': 'Given once.',
  'scenarios.prompt': 'Who gives way?',
  'scenarios.wind': 'Wind from {wind} degrees. Your course: {heading} degrees.',
  'giveway.own': 'I give way',
  'giveway.other': 'The other vessel gives way',
  'giveway.both': 'Both act',
  'giveway.neither': 'Neither has to give way',
  'signals.group.manoeuvre': 'Manoeuvring',
  'signals.group.manoeuvre.hint': 'In sight of one another, Rule 34(a)',
  'signals.group.warning': 'Warning',
  'signals.group.warning.hint': 'Doubt about the other vessel, Rule 34(d)',
  'signals.group.narrow-channel': 'Narrow channel',
  'signals.group.narrow-channel.hint': 'Overtaking and bend signal, Rule 34(c) and (e)',
  'signals.group.restricted-visibility': 'Restricted visibility',
  'signals.group.restricted-visibility.hint': 'Fog and haze, Rule 35',
  'signals.group.identity': 'Identity',
  'signals.group.identity.hint': 'Who am I, Rule 35(k)',
  'signals.group.distress': 'Distress',
  'signals.group.distress.hint': 'Rule 37 with Annex IV',
  'signals.step.short': 'short',
  'signals.step.prolonged': 'prolonged',
  'signals.step.continuous': 'continuous',
  'signals.step.bell': 'bell',
  'signals.step.gong': 'gong',
  'signals.step.silence': 'pause',
  'signals.length': '{steps} · {seconds} seconds',
  'signals.play': 'Play',
  'signals.stop': 'Stop',
  'signals.repeat.once': 'Once, as the situation requires.',
  'signals.repeat.minute': 'Repeated every minute while the situation lasts.',
  'signals.repeat.minutes': 'Repeated every {minutes} minutes while the situation lasts.',
  'signals.voice.gross': 'Vessel 200 m or more',
  'signals.voice.mittel': 'Vessel 75 m to 200 m',
  'signals.voice.klein': 'Vessel under 75 m',
  'signals.voice.title': '{low} to {high} hertz under Annex III',
  'signals.voice.note':
    'Pitch {hz} hertz. Annex III prescribes {low} to {high} hertz for a {name}, so that the pitch tells you the size of the other vessel.',
  'signals.disclaimer':
    'The sound is generated in the browser from the pattern in the rule, it is not a recording of a ship. A learning tool, not a substitute for navigation.',
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.language.de': 'Deutsch',
  'settings.language.en': 'English',
  'settings.region': 'IALA region',
  'settings.region.hint':
    'The data follows two sources: region A the German SeeSchStrO, region B the US 33 CFR 62. Which region applies where is on the chart.',
  'settings.units': 'Units',
  'settings.units.nm': 'Nautical miles',
  'settings.units.km': 'Kilometres',
  'binnenSignale.wer.bergfahrer': 'Given by the vessel travelling upstream: on a river towards the source.',
  'binnenSignale.wer.talfahrer': 'Given by the vessel travelling downstream: on a river with the current.',
  'binnenSignale.wer.ueberholender': 'Given by the vessel that wants to overtake.',
  'binnenSignale.wer.vorausfahrender': 'Given by the vessel that is to be overtaken.',
  'binnenSignale.antworten': 'Possible answers',
  'binnenSignale.fahrzeug.maschine': 'Motorised vessel',
  'binnenSignale.fahrzeug.klein': 'Small craft',
  'binnenSignale.antwortAuf': 'Answer to:',
  'binnenSignale.schweigen': 'No signal: here silence means consent, and the regulation says so; it is not a gap in the table.',
  'binnenSignale.schweigen.kurz': 'No signal',
  'binnenSignale.minute': 'Repeated at intervals of not more than one minute.',
  'binnenSignale.wiederholt': 'Repeated as long as the situation lasts.',
  'binnenSignale.licht': 'A motorised vessel gives a yellow light signal of the same length with it, bright and visible from all sides (§ 4.01 No. 2).',
  'binnenSignale.licht.glocke': 'No light signal: bell signals are exempt under § 4.01 No. 2.',
  'binnenSignale.licht.schweigen': 'No light signal: where there is no sound signal, there is none.',
  'binnenSignale.kleinfahrzeug.ja': 'A small craft may give this signal too (§ 4.02 No. 2).',
  'binnenSignale.kleinfahrzeug.nein': 'A small craft does not give this signal: § 4.02 No. 2 allows it only the general signals of section A.',
  'signals.auftrag.binnen': 'Tap a signal, listen to it and read who gives it and what follows.',
  'settings.mondphase': 'Moon phase',
  'viewer.mondphase.hinweis':
    'The moon decides how hard it gets: at full moon you see the shape of the vessel, at new moon only her lights. That is what it looks like on the water.',
  'settings.mondphase.hint':
    'The moon decides how much of the hull you see at night. At new moon only the lights remain, and that is what this is about.',
  'mondphase.neu': 'New moon',
  'mondphase.sichel': 'Crescent',
  'mondphase.halb': 'Half moon',
  'mondphase.dreiviertel': 'Gibbous',
  'mondphase.voll': 'Full moon',
  'settings.view': 'View',
  'settings.reset': 'Delete learning progress',
  'settings.reset.done': 'Progress deleted.',
  'settings.storage': 'Everything stays on this device. The app sends nothing to the network.',
  'settings.offline.ready': 'Ready for use without a network.',
  'settings.offline.pending': 'Preparing for use without a network.',
  'settings.offline.off': 'Not available offline: this browser cannot do it.',
  'nav.cards': 'Cards',
  'cards.prompt': 'What does this picture show?',
  'cards.prompt.signal': 'Which signal is this?',
  'cards.reveal': 'Reveal',
  'cards.known': 'I knew it',
  'cards.unknown': 'I did not',
  'app.disclaimer':
    'A learning tool, not a substitute for navigation. Everything here comes from public-domain rule texts and carries its citation.',
  'exam.title': 'Exam mode',
  'exam.intro':
    'A paper like the official exam: {questions} questions, {minutes} minutes, four answers each, one of them correct. The questions come from this app, not from the official catalogue.',
  'exam.part': '{name}: {questions} questions, pass from {min} points',
  'exam.skipped':
    'Not included: {name} with {questions} questions. That part needs a chart, dividers and a plotter, and this app does not do that work for you.',
  'exam.source': 'Numbers from: {title}, as of {stand}.',
  'exam.area.see': 'Sea',
  'exam.area.binnen': 'Inland waterways',
  'exam.binnen.note':
    'The specific part draws its questions from the German inland waterway rules (BinSchStrO), chapter 3, each with its citation. The base part comes from the shared ground, lights and sound signals: the sound signals of the inland rules are in chapter 4 and are not in the stock yet.',
  'exam.start': 'Start a paper',
  'exam.position': 'Question {at} of {of} · {part}',
  'exam.back': 'Back',
  'exam.next': 'Next',
  'exam.finish': 'Hand in',
  'exam.open': 'still open: {open}',
  'exam.passed': 'Passed.',
  'exam.failed': 'Not passed.',
  'exam.result.part': '{name}: {points} of {questions} points, {min} were needed',
  'exam.result.points': 'Together {points} of {questions} points. Each part counts on its own.',
  'exam.citations': 'Where the questions came from',
  'exam.wrong': 'Not yet sitting',
  'exam.again': 'Another paper',
  'nav.exam': 'Exam',
  'paywall.title': 'Full version',
  'paywall.body':
    'For free you get the light pictures of rules 23 to 31 and ten practice questions. Marks, give-way situations, sound signals, progress and the full question set belong to the full version.',
  'paywall.price': 'One payment of {price} euro, no subscription.',
  'paywall.stub':
    'The purchase will run through the store later. In this build the button unlocks without payment.',
  'paywall.buy': 'Unlock the full version',
  'paywall.restore': 'Restore purchase',
  'paywall.unlocked': 'The full version is unlocked.',
  'paywall.quiz.limit': 'The ten free questions are used up.',
}

const DICTS: Record<Lang, Record<Key, string>> = { de, en }

/** Ein Text der Oberfläche, mit eingesetzten Platzhaltern ("{count}"). */
export function t(lang: Lang, key: Key, vars: Record<string, string | number> = {}): string {
  const template = DICTS[lang][key]
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  )
}

/** Aus einem zweisprachigen Datensatz die passende Sprache. */
export function pick(text: { de: string; en: string }, lang: Lang): string {
  return lang === 'en' ? text.en : text.de
}

/** Platzhalter eines Eintrags, für die Prüfung der Wörterbücher. */
export function placeholders(value: string): string[] {
  return [...value.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!).sort()
}
